import apiClient from '@/services/apiClient'
import type {BenchmarkThresholdRequest} from '@/components/SelfOnboarding/ProgramDashboardV2/helpers/benchmarkThresholdUtils'

const PRODUCT_DASHBOARD_SEARCH_API = '/productDashboards/searchByName/'
const PRODUCT_DASHBOARD_CREATE_API = '/cmdb/configItem'
const PRODUCT_APPD_ACCOUNTS_API = '/productDashboards/se-metrics/appD/accounts'

export async function getProductDashboardsSearchByName(search: string) {
  const response = await apiClient.get(PRODUCT_DASHBOARD_SEARCH_API, {
    params: {search},
  })
  return response.data
}

export interface ProgramDashboardMutationResponse {
  // Create (POST /cmdb/configItem) returns the new id as `id`; other flows expose productDashboardId.
  id?: string
  productDashboardId?: string
  [key: string]: unknown
}

export async function createProgramDashboard(payload: any): Promise<ProgramDashboardMutationResponse> {
  const response = await apiClient.post(PRODUCT_DASHBOARD_CREATE_API, payload)
  return response.data
}

export async function updateProgramDashboard(id: string, payload: any): Promise<ProgramDashboardMutationResponse> {
  const response = await apiClient.put(`/cmdb/configItem/${id}`, payload)
  return response.data
}

export async function createProgramBenchmarkThresholds(productDashboardId: string, payload: BenchmarkThresholdRequest) {
  const response = await apiClient.post(`/productDashboards/${productDashboardId}/benchmark-thresholds`, payload)
  return response.data
}

export async function patchProgramBenchmarkThresholds(productDashboardId: string, payload: BenchmarkThresholdRequest) {
  const response = await apiClient.patch(`/productDashboards/${productDashboardId}/benchmark-thresholds`, payload)
  return response.data
}

export async function getProgramBenchmarkThresholds(productDashboardId: string) {
  const response = await apiClient.get(`/productDashboards/${productDashboardId}/benchmark-thresholds`)
  return response.data
}

// ─── Global SE Metrics KPI Benchmark APIs ────────────────────────────────────

export interface BenchmarkMetadata {
  id?: string
  industrySegment?: string
  aiAdoption?: number | null
  functionalTestAutomation?: number | null
  overallAutomationCoverage?: number | null
  escapeDefects?: number | null
  unitTestCoverage?: number | null
  systemAvailability?: number | null
  changeFailureRate?: number | null
  buildSuccessRate?: number | null
  gtAdoptionRate?: number | null
  prCycleTime?: number | null
  prThroughput?: number | null
  deploymentFrequency?: number | null
  appResponseTime?: number | null
  leadTimeToChange?: number | null
  meanTimeToResolve?: number | null
  commits?: number | null
  pulls?: number | null
  totalVulnerability?: number | null
  appCrashRate?: number | null
}

/** Keys of BenchmarkMetadata that hold a numeric KPI value (excludes id/industrySegment/metrics). */
export type BenchmarkMetricKey = {
  [K in keyof BenchmarkMetadata]-?: NonNullable<BenchmarkMetadata[K]> extends number ? K : never
}[keyof BenchmarkMetadata]

export interface GlobalBenchmarkMetric {
  externalBenchmark?: number | null
  displayName?: string
  unit?: string
  benchmarkSource?: string
}

// Backend POJO includes metadata and a metrics hashmap — keep flat fields for form usage
// and also expose the raw metrics map when present.
export interface BenchmarkMetadata {
  benchmarkLevel?: string
  description?: string
  metrics?: Record<string, GlobalBenchmarkMetric>
}

const METRIC_KEY_MAP: Record<string, string> = {
  id: 'ID',
  aiAdoption: 'AI_ADOPTION',
  functionalTestAutomation: 'TEST_AUTOMATION',
  overallAutomationCoverage: 'OVERALL_AUTOMATION_COVERAGE',
  escapeDefects: 'ESCAPE_DEFECTS',
  unitTestCoverage: 'UNIT_TEST_COVERAGE',
  systemAvailability: 'SYSTEM_AVAILABILITY',
  appCrashRate: 'APP_CRASH_RATE',
  changeFailureRate: 'CHANGE_FAIL_RATE',
  buildSuccessRate: 'BUILD_SUCCESS_RATE',
  gtAdoptionRate: 'GT_ADOPTION_RATE',
  prCycleTime: 'PR_CYCLE_TIME',
  prThroughput: 'PR_THROUGHPUT',
  deploymentFrequency: 'DEPLOYMENT_FREQUENCY',
  appResponseTime: 'APP_RESPONSE_TIME',
  leadTimeToChange: 'LEAD_TIME_TO_CHANGE',
  meanTimeToResolve: 'MEAN_TIME_TO_RESOLVE',
  commits: 'COMMITS',
  pulls: 'PULL_REQUESTS',
  totalVulnerability: 'APPSECFLOW_VULNERABILITY',
}

const METRIC_DISPLAY_MAP: Record<string, {displayName: string; unit?: string}> = {
  AI_ADOPTION: {displayName: 'AI Adoption %', unit: '%'},
  TEST_AUTOMATION: {displayName: 'Functional Test Automation %', unit: '%'},
  OVERALL_AUTOMATION_COVERAGE: {displayName: 'Overall Automation Coverage %', unit: '%'},
  ESCAPE_DEFECTS: {displayName: 'Escape Defects %', unit: '%'},
  UNIT_TEST_COVERAGE: {displayName: 'Unit Test Coverage %', unit: '%'},
  SYSTEM_AVAILABILITY: {displayName: 'System Availability %', unit: '%'},
  CHANGE_FAIL_RATE: {displayName: 'Change Failure Rate %', unit: '%'},
  BUILD_SUCCESS_RATE: {displayName: 'Build Success Rate %', unit: '%'},
  GT_ADOPTION_RATE: {displayName: 'GT Adoption Rate', unit: '%'},
  PR_CYCLE_TIME: {displayName: 'PR Cycle Time', unit: 'hours'},
  PR_THROUGHPUT: {displayName: 'PR Throughput', unit: 'count'},
  DEPLOYMENT_FREQUENCY: {displayName: 'Deployment Frequency', unit: 'deployments/week'},
  APP_RESPONSE_TIME: {displayName: 'App Response Time', unit: 'milliseconds'},
  LEAD_TIME_TO_CHANGE: {displayName: 'Lead Time to Change', unit: 'days'},
  MEAN_TIME_TO_RESOLVE: {displayName: 'Mean Time To Resolve', unit: 'hours'},
  COMMITS: {displayName: 'Commits', unit: 'count'},
  PULL_REQUESTS: {displayName: 'Pull Requests', unit: 'count'},
  APPSECFLOW_VULNERABILITY: {displayName: 'Vulnerability Target', unit: 'count'},
  APP_CRASH_RATE: {displayName: 'App Crash Rate %', unit: '%'},
}

// Default constants used for POST /benchmark payloads
const DEFAULT_BENCHMARK_LEVEL = 'GLOBAL'
const DEFAULT_DESCRIPTION = 'Global external benchmark values maintained by Admin users.'

function buildGlobalBenchmarkRequest(payload: BenchmarkMetadata) {
  const metrics: Record<string, any> = {}

  for (const key of Object.keys(METRIC_KEY_MAP) as string[]) {
    if (key === 'id') continue
    const metricKey = METRIC_KEY_MAP[key]
    const value = (payload as any)[key]
    const meta = METRIC_DISPLAY_MAP[metricKey]

    metrics[metricKey] = {
      externalBenchmark: value ?? null,
      displayName: meta.displayName,
      unit: meta.unit,
    }
  }

  return {
    benchmarkLevel: DEFAULT_BENCHMARK_LEVEL,
    description: DEFAULT_DESCRIPTION,
    metrics,
  }
}

// Convert backend benchmark (with metrics map) to flat form used by UI
const convertBackendToFlat = (backend: any): BenchmarkMetadata => {
  if (!backend) return backend
  const result: any = {
    id: backend.id,
    industrySegment: backend.industrySegment,
    benchmarkLevel: backend.benchmarkLevel,
    description: backend.description,
  }

  // build reverse map metricKey -> formKey
  const reverseMap: Record<string, string> = Object.entries(METRIC_KEY_MAP).reduce(
    (acc, [k, v]) => {
      acc[v] = k
      return acc
    },
    {} as Record<string, string>,
  )

  const metrics = backend.metrics || {}
  for (const metricKey of Object.keys(metrics)) {
    const formKey = reverseMap[metricKey]
    if (formKey) {
      result[formKey] = metrics[metricKey]?.externalBenchmark ?? null
    }
  }

  return result
}

export async function createGlobalBenchmark(payload: BenchmarkMetadata): Promise<BenchmarkMetadata> {
  const body = buildGlobalBenchmarkRequest(payload)
  const response = await apiClient.post('/benchmark', body)
  return convertBackendToFlat(response.data)
}

export async function getGlobalBenchmarks(industrySegment?: string): Promise<BenchmarkMetadata[]> {
  const response = await apiClient.get('/benchmark', {
    params: industrySegment ? {industrySegment} : undefined,
  })
  const data = response.data
  const records = Array.isArray(data) ? data : data == null ? [] : [data]
  return records.map(item => (item?.metrics ? convertBackendToFlat(item) : item))
}

export async function patchGlobalBenchmark(id: string, payload: BenchmarkMetadata): Promise<BenchmarkMetadata> {
  const body = buildGlobalBenchmarkRequest(payload)
  const response = await apiClient.patch(`/benchmark/${id}`, body)
  return convertBackendToFlat(response.data)
}

export async function deleteGlobalBenchmark(id: string): Promise<void> {
  await apiClient.delete(`/benchmark/${id}`)
}

export async function fetchAppDAccounts() {
  const response = await apiClient.get(PRODUCT_APPD_ACCOUNTS_API)
  return response.data
}

export async function fetchDirectReportees(l0OrgHeadEmail: string) {
  const response = await apiClient.get('/users/manager/direct-reportees', {
    params: {email: l0OrgHeadEmail},
  })
  return response.data
}

export async function fetchHierarchicalReportees(managerEmail: string) {
  const response = await apiClient.get('/users/manager/hierarchical-reportees', {
    params: {email: managerEmail},
  })
  return response.data
}

export async function fetchHierarchicalReporteesOptions(l1LeaderEmail: string): Promise<{
  options: {id: string; displayText: string; email: string; name: string}[]
  noReporteesError: string | null
}> {
  const data = await fetchHierarchicalReportees(l1LeaderEmail)
  const reportees = Array.isArray(data) ? data : []

  if (reportees.length === 0) {
    return {
      options: [],
      noReporteesError: l1LeaderEmail,
    }
  }

  const options = reportees.map((item: any) => ({
    id: item.email,
    displayText: item.email,
    email: item.email,
    name: item.name,
  }))

  return {
    options,
    noReporteesError: null,
  }
}
